import {
  Alert,
  Box,
  CardActions,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Snackbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Check,
  CheckCircleOutlined,
  Close,
  ContentCopy,
  DoNotDisturb,
  Preview,
  Public,
} from '@mui/icons-material';
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import TrustpageService from '../../services/TrustpageService';
import trustpageService from '../../services/TrustpageService';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { format } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { ITrustpageEntry } from '../../shared/interfaces/ITrustpageEntry';
import styled from 'styled-components';
import EditIcon from '@mui/icons-material/Edit';
import TrustpageItem from '../TrustpageItem/TrustpageItem';
import PlatformService from '../../services/PlatformService';
import { IMonitoring } from '../../shared/interfaces/IMonitoring';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import { IGMBLocationsResponse } from '../../shared/interfaces/IGMBLocationsResponse';
import GMBService from '../../services/GMBService';
import { IPlatform } from '../../shared/interfaces/IPlatform';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { AuthContext } from '../../providers/AuthProvider';
import Confirm from '../Confirm/Confirm';

interface Props {
  trustpage: ITrustpageEntry;
  customFontSet: boolean;
  companyLogoSet: boolean;
  trustpages: ITrustpageEntry[];
  reload: () => Promise<void>;
}

interface CardContentRowProps {
  children: ReactNode;
}

const ContentContainer = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing(2)};
  grid-template-columns: repeat(1, 3fr);
`;

const CardContentRow = ({ children }: CardContentRowProps) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      {children}
    </Box>
  );
};

const TrustpageEntry = ({ trustpage, customFontSet, companyLogoSet, reload }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const [trustpageSettings, setTrustpageSettings] = useState<ITrustpageEntry>(trustpage);
  const {
    companyName,
    companyWebsiteUrl,
    primaryColor,
    secondaryColor,
    lang,
    createdAt,
    slug,
    id,
    monitorings,
    score,
  } = trustpageSettings;

  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [isTrustpageUrlCopied, setIsTrustpageUrlCopied] = useState<boolean>(false);
  const [isCompanyWebsiteUrlCopied, setIsCompanyWebsiteUrlCopied] = useState<boolean>(false);
  const [isPrimaryColorCopied, setIsPrimaryColorCopied] = useState<boolean>(false);
  const [isSecondaryColorCopied, setIsSecondaryColorCopied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [configuredMonitorings, setConfiguredMonitorings] = useState<IMonitoring[]>([]);
  const [selectedMonitorings, setSelectedMonitorings] = useState<any[]>([]);
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowAlert(false);
  };

  const createURL = useCallback(
    (slug: string | null, id: string) => {
      const startURL = `${process.env.REACT_APP_FRONTEND_BASE_URL || 'http://localhost:3000'}`;
      return slug
        ? `${startURL}/trustpages/${slug}?lang=${lang === 'gb' ? 'en' : lang}`
        : `${startURL}/trustpages/${id}?lang=${lang === 'gb' ? 'en' : lang}`;
    },
    [lang]
  );

  const handleTruatpageSeal = async (id: string) => {
    try {
      const response = await TrustpageService.getTrustpageSeal(id);
      if (!response) {
        console.error('No response received or response not OK');
        return;
      }

      const url = `${process.env.REACT_APP_BASE_URL}/trustpages/seal/${id}.png`;
      const link = document.createElement('a');

      const imageResponse = await fetch(url);
      const blob = await imageResponse.blob();

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;

        if (!base64String.startsWith('data:image/')) {
          console.error('Invalid Base64 string');
          return;
        }

        await navigator.clipboard.writeText(base64String);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 4000);

        link.href = base64String;
        link.download = 'trustpage-seal.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to download Trustpage Seal: ', error);
    }
  };

  const handleCopyToClipboardClick = useCallback(
    async (text: string, setSuccessfullyCopiedLabel: Dispatch<SetStateAction<boolean>>) => {
      try {
        await navigator.clipboard.writeText(text);
        setSuccessfullyCopiedLabel(true);
        setTimeout(() => setSuccessfullyCopiedLabel(false), 3000);
      } catch (error) {
        console.error('Failed to copy text: ', error);
      }
    },
    []
  );

  const deleteTrustPageWithId = async (id: string) => {
    const success = await TrustpageService.deleteTrustpage(id);
    if (success) {
      await reload();
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const getTrustpageSettings = async () => {
      const trustpageSettings = await TrustpageService.getTrustpage(
        id,
        setLoading,
        controller.signal
      );

      if (!trustpageSettings) return;

      setTrustpageSettings(trustpageSettings);
    };

    (async () => await getTrustpageSettings())();

    return () => {
      controller.abort();
    };
  }, [id]);

  const updatedTrustpage = useCallback(async () => {
    if (!trustpageSettings) return;
    setLoading(true);
    const updatedSeal = {
      ...trustpageSettings.seal,
      generatedAt: createdAt,
      lastScore: score,
      sealFile: `trust_${id}.png`,
    };

    const updatedSettings = {
      ...trustpageSettings,
      seal: updatedSeal,
    };

    const trustpage = await trustpageService.updateTrustpage(id, updatedSettings);

    setLoading(false);

    await reload();
    if (!trustpage) return;
    setTrustpageSettings(trustpage);
  }, [id, trustpageSettings]);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);
      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };

    const getMonitorings = async () => {
      let monitorings: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
        false,
        'both',
        setLoading,
        controller.signal
      );

      if (!monitorings) return;

      const gmbMonitoredLocations: IGMBLocationsResponse = await GMBService.getAllLocations(
        undefined,
        1000,
        setLoading,
        controller.signal,
        true
      );

      if (!gmbMonitoredLocations) return;

      const hasGmbEntries = gmbMonitoredLocations.locations.length > 0;

      if (hasGmbEntries) {
        for (const location of gmbMonitoredLocations.locations) {
          const mon: IMonitoring = {
            company: location.locationID,
            createdAt: '',
            deletedAt: '',
            id: location.ID,
            jobs: [],
            platformId: -2,
            label: location.locationData.title,
            url: '',
            updatedAt: '',
            communityManagement: location.communityManagement,
            isBenchmark: false,
            type: 'LOCATION',
          };
          monitorings.push(mon);
        }

        setPlatforms((prevState) => [...prevState, PlatformService.getPseudoGoogle()]);
      }

      if (monitorings.length === 0) return;

      setConfiguredMonitorings(monitorings);
      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitorings.find((m) => m.platformId === v.id));
      });
    };

    (async () => {
      await getPlatforms();
      await getMonitorings();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <ContentContainer>
      <Card sx={{ minWidth: 350, height: 'fit-content' }}>
        {!edit ? (
          <>
            <CardHeader
              disableTypography
              title={<Typography variant="body1">{companyName}</Typography>}
              subheader={
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {`${t('created')} ${format(new Date(createdAt), 'dd.MM.yyyy')}`}
                </Typography>
              }
              action={
                <Tooltip
                  title={t('copy_trustpage_url_caption')}
                  arrow
                >
                  <IconButton
                    aria-label={t('copy_trustpage_url_caption')}
                    onClick={async () =>
                      handleCopyToClipboardClick(createURL(slug, id), setIsTrustpageUrlCopied)
                    }
                  >
                    {isTrustpageUrlCopied ? (
                      <CheckCircleOutlined
                        color="primary"
                        fontSize="small"
                      />
                    ) : (
                      <ContentCopy
                        color="primary"
                        fontSize="small"
                      />
                    )}
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider orientation="horizontal" />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Typography variant="body1">{t('preset_title')}</Typography>
              <CardContentRow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('website')}:
                </Typography>
                {isCompanyWebsiteUrlCopied ? (
                  <CheckCircleOutlined
                    color="primary"
                    fontSize="small"
                  />
                ) : (
                  <Tooltip
                    title={companyWebsiteUrl}
                    placement="left"
                    arrow
                  >
                    <Public
                      fontSize="small"
                      color="primary"
                      sx={{ cursor: 'pointer' }}
                      onClick={async () =>
                        await handleCopyToClipboardClick(
                          companyWebsiteUrl,
                          setIsCompanyWebsiteUrlCopied
                        )
                      }
                    />
                  </Tooltip>
                )}
              </CardContentRow>
              <CardContentRow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('custom_logo_label')}:
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {companyLogoSet ? t('yes') : t('no')}
                </Typography>
              </CardContentRow>
              <CardContentRow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('custom_primary_color_label')}:
                </Typography>
                {primaryColor ? (
                  <>
                    {isPrimaryColorCopied ? (
                      <CheckCircleOutlined
                        color="primary"
                        fontSize="small"
                      />
                    ) : (
                      <Tooltip
                        title={primaryColor}
                        placement="left"
                        arrow
                      >
                        <Box
                          sx={{
                            width: '32px',
                            height: '16px',
                            backgroundColor: primaryColor,
                            borderRadius: `${theme.shape.borderRadius}px`,
                            cursor: 'pointer',
                          }}
                          onClick={async () =>
                            await handleCopyToClipboardClick(primaryColor, setIsPrimaryColorCopied)
                          }
                        />
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <DoNotDisturb
                    color="disabled"
                    fontSize="small"
                  />
                )}
              </CardContentRow>
              <CardContentRow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('custom_secondary_color_label')}:
                </Typography>
                {secondaryColor ? (
                  <>
                    {isSecondaryColorCopied ? (
                      <CheckCircleOutlined
                        color="primary"
                        fontSize="small"
                      />
                    ) : (
                      <Tooltip
                        title={secondaryColor}
                        placement="left"
                        arrow
                      >
                        <Box
                          sx={{
                            width: '32px',
                            height: '16px',
                            backgroundColor: secondaryColor,
                            borderRadius: `${theme.shape.borderRadius}px`,
                            cursor: 'pointer',
                          }}
                          onClick={async () =>
                            await handleCopyToClipboardClick(
                              secondaryColor,
                              setIsSecondaryColorCopied
                            )
                          }
                        />
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <DoNotDisturb
                    color="disabled"
                    fontSize="small"
                  />
                )}
              </CardContentRow>
              <CardContentRow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('custom_font_label')}:
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {customFontSet ? t('yes') : t('no')}
                </Typography>
              </CardContentRow>
              <CardContentRow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('language')}:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {lang}
                  </Typography>
                  <img
                    src={`https://flagcdn.com/16x12/${lang === 'en' ? 'gb' : lang}.png`}
                    srcSet={`https://flagcdn.com/32x24/${
                      lang === 'en' ? 'gb' : lang
                    }.png 2x, https://flagcdn.com/48x36/${lang === 'en' ? 'gb' : lang}.png 3x`}
                    width="16"
                    height="12"
                    referrerPolicy="no-referrer"
                    alt={lang}
                  />
                </Box>
              </CardContentRow>
              <CardContentRow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t('monitoring')}:
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {monitorings ? monitorings.length : <CircularProgress size={16} />}
                </Typography>
              </CardContentRow>
            </CardContent>
          </>
        ) : (
          <TrustpageItem
            trustpage={trustpageSettings}
            edit={edit}
            onChange={setTrustpageSettings}
            onMonitoringChange={setSelectedMonitorings}
            selectedMonitorings={selectedMonitorings}
            configuredMonitorings={configuredMonitorings}
          />
        )}
        <Divider orientation="horizontal" />
        <CardActions
          disableSpacing
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={t('goto_website_label')}
              arrow
            >
              <Link
                href={companyWebsiteUrl}
                underline="none"
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'flex', m: theme.spacing(1), alignItems: 'center' }}
              >
                <Public
                  fontSize="small"
                  color="primary"
                />
              </Link>
            </Tooltip>
            <Tooltip
              title={t('goto_trustpage_label')}
              arrow
            >
              <Link
                href={createURL(slug, id)}
                underline="none"
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'flex', m: theme.spacing(1), alignItems: 'center' }}
              >
                <Preview fontSize="small" />
              </Link>
            </Tooltip>
            {hasRole('can-use-seal') && (
              <>
                <Tooltip
                  title={'Trustsiegel Code-Snippet zur Einbindung auf Ihrer Webseite herunterladen'}
                  arrow
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleTruatpageSeal(id)}
                  >
                    <DownloadForOfflineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!edit ? (
              <IconButton
                size="small"
                disabled={!monitorings}
                onClick={() => {
                  setSelectedMonitorings(monitorings.map((m) => m.id));
                  setEdit(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            ) : (
              <>
                {loading ? (
                  <CircularProgress
                    sx={{ ml: 1 }}
                    size={18}
                  />
                ) : (
                  <IconButton
                    size="small"
                    disabled={loading}
                    onClick={updatedTrustpage}
                  >
                    <Check
                      sx={{ color: 'primary.main' }}
                      fontSize="small"
                    />
                  </IconButton>
                )}
              </>
            )}
            {!edit ? (
              <>
                <IconButton
                  color="error"
                  aria-label={t('delete_trustpage_caption')}
                  onClick={() => setDeleteIsOpen(true)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <Confirm
                  text={t('delete_confirm')}
                  open={deleteIsOpen}
                  onCancel={() => setDeleteIsOpen(false)}
                  onOk={async () => await deleteTrustPageWithId(id)}
                />
              </>
            ) : (
              <IconButton
                size="small"
                onClick={() => {
                  setEdit(false);
                }}
              >
                <Close
                  color="error"
                  fontSize="small"
                />
              </IconButton>
            )}
            {showAlert && (
              <Snackbar
                open={showAlert}
                autoHideDuration={10000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <Alert
                  onClose={handleClose}
                  severity="success"
                  variant="filled"
                  sx={{ width: '100%' }}
                >
                  Dein Siegel wurde in die Zwischenablage Kopiert!
                </Alert>
              </Snackbar>
            )}
          </Box>
        </CardActions>
      </Card>
    </ContentContainer>
  );
};

export default TrustpageEntry;
